import { Progress, createStyles } from '@mantine/core';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import { useIsMutating } from '@tanstack/react-query';

const ARM_DELAY_MS = 200;
const MID_ANIMATION_MS = 500;
const FINISH_ANIMATION_MS = 300;

const bgAnimation = keyframes`
  0% {
    background-position: 0% 0%;
  }

  50% {
    background-position: 100% 0%;
  }

  100% {
    background-position: 0% 0%;
  }
`;

const useStyles = createStyles((theme) => ({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 9999,
    opacity: 0,
    transition: 'opacity 250ms ease',
    pointerEvents: 'none',

    '&.isLoading': {
      opacity: 1,
    },
  },
  bar: {
    background: theme.fn.gradient({
      from: theme.colors.splash[5],
      to: theme.colors.splash[2],
    }),
    backgroundSize: '200% 200%',
    transition: 'width 500ms ease-in-out',
    animation: `${bgAnimation} 2s ease infinite`,
  },
  root: {
    background: 'transparent',
  },
}));

export function RouteChangeOrMutationProgressBar() {
  const router = useRouter();
  const { classes, cx } = useStyles();

  const isMutating = useIsMutating();
  const [isRouteChanging, setIsRouteChanging] = useState(false);

  const asPathRef = useRef(router.asPath);
  asPathRef.current = router.asPath;

  useEffect(() => {
    const { events } = router;

    const handleStart = (url: string, { shallow }: { shallow: boolean }) => {
      if (shallow || url === asPathRef.current) return;
      setIsRouteChanging(true);
    };
    const handleEnd = () => setIsRouteChanging(false);

    events.on('routeChangeStart', handleStart);
    events.on('routeChangeComplete', handleEnd);
    events.on('routeChangeError', handleEnd);

    return () => {
      events.off('routeChangeStart', handleStart);
      events.off('routeChangeComplete', handleEnd);
      events.off('routeChangeError', handleEnd);
    };
  }, [router]);

  const isPending = isRouteChanging || isMutating > 0;

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isPending) {
      setIsVisible(false);
      return undefined;
    }

    const armTimer = setTimeout(() => setIsVisible(true), ARM_DELAY_MS);
    return () => clearTimeout(armTimer);
  }, [isPending]);

  const [width, setWidth] = useState(0);
  const hasPaintedRef = useRef(false);

  useEffect(() => {
    if (isVisible) {
      hasPaintedRef.current = true;
      setWidth((v) => (v > 30 ? v : 30));

      const middleAnimationTimer = setTimeout(() => {
        setWidth((v) => (v > 70 ? v : 70));
      }, MID_ANIMATION_MS);

      return () => clearTimeout(middleAnimationTimer);
    }

    if (!hasPaintedRef.current) return undefined;
    hasPaintedRef.current = false;

    setWidth(100);
    const finishAnimationTimer = setTimeout(() => setWidth(0), FINISH_ANIMATION_MS);
    return () => clearTimeout(finishAnimationTimer);
  }, [isVisible]);

  return (
    <div className={cx(classes.container, { isLoading: isVisible })}>
      <Progress
        value={width}
        w="100%"
        radius={0}
        size="sm"
        classNames={{ bar: classes.bar, root: classes.root }}
      />
    </div>
  );
}
